// Shared lifecycle-stage badge. Renders a `<Pill>` with the stage's
// human-readable label and a semantic intent. The intent map is
// centralised here so contact list / detail / bulk-edit surfaces stay
// visually consistent.
//
// Intent rationale (per frontend/CLAUDE.md §Hard rules #2):
// - cold (was: new)       → info (cold lead, informational)
// - contacted             → neutral (colorless state)
// - qualified, opportunity_open → accent (active progress)
// - disqualified          → danger
// - customer              → ok
// - callback_scheduled    → warn (action awaited at a future time)
// - in_sequence, nurture  → info (long-running engagement)
// - engaged (LT-358)      → accent (active progress, positive signal)
// - no_answer (LT-358)    → warn (follow-up due)
// - dormant (LT-358)      → neutral (inactive, no current motion)

import React from 'react';

import { Pill, type PillIntent } from '../ui';
import {
  LIFECYCLE_STAGE_LABELS,
  type LifecycleStage,
} from '../types/contact';

const LIFECYCLE_INTENT: Record<LifecycleStage, PillIntent> = {
  cold: 'info',
  contacted: 'neutral',
  qualified: 'accent',
  disqualified: 'danger',
  customer: 'ok',
  in_sequence: 'info',
  callback_scheduled: 'warn',
  opportunity_open: 'accent',
  nurture: 'info',
  engaged: 'accent',
  no_answer: 'warn',
  dormant: 'neutral',
};

export interface LifecyclePillProps {
  stage: LifecycleStage | string | null | undefined;
}

export const LifecyclePill: React.FC<LifecyclePillProps> = ({ stage }) => {
  if (!stage) return <span className="text-slate-500">—</span>;
  const known = stage in LIFECYCLE_INTENT
    ? (stage as LifecycleStage)
    : null;
  const intent: PillIntent = known ? LIFECYCLE_INTENT[known] : 'neutral';
  const label = known ? LIFECYCLE_STAGE_LABELS[known] : String(stage);
  return <Pill intent={intent}>{label}</Pill>;
};

export default LifecyclePill;
